import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Box } from "@mui/material";
import {
  PamGrid,
  useNotification,
  useRowSelection,
  ConfirmationDialog,
  formatDate,
  formatDateTimeForDisplay,
  GridConfigMenu,
  useGridConfigs,
  notifyPermissionError,
} from "@1823-partners/core";
import { SetFilterModule } from "ag-grid-enterprise";

import api, { fetchExcludedPositions, deleteExclusions } from "../api";

// Row ID generator for ExcludedPositions
const getExcludedRowId = (data) =>
  `${data.CUSTODIAN || ''}_${data.SOURCE_ACCOUNT || ''}_${data.IDENTIFIER || ''}_${data.ACCOUNT_ID || ''}_${data.CUSTODY_IDENTIFIER || ''}_${data.ASOFDT || ''}`;

const ExcludedPositions = ({ userId, height = 500, refreshKey = 0 }) => {
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const gridRef = useRef(null);
  const { showNotification } = useNotification();

  // 1823-core row selection — selectedRows is a Set keyed by getRowId, which is
  // what PamGrid's native showSelectionColumn expects (it calls .has()).
  const {
    selectedRows,
    setSelectedRows,
    clearSelection,
    selectedCount,
    getSelectedData,
  } = useRowSelection({ getRowId: getExcludedRowId });

  const loadEntries = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetchExcludedPositions();
      const formattedData = Array.isArray(response.data) ? response.data : [];
      setRowData(formattedData);
      clearSelection();
    } catch (error) {
      console.error("Error fetching excluded positions:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to load excluded positions. Please try again.", 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [clearSelection, showNotification]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Refresh when refreshKey changes
  useEffect(() => {
    if (refreshKey > 0) {
      loadEntries();
    }
  }, [refreshKey, loadEntries]);

  const columnDefs = useMemo(() => [
    { field: 'CUSTODIAN', headerName: 'Rec Cust', editable: false, filter: 'agSetColumnFilter' },
    { field: 'SOURCE_ACCOUNT', headerName: 'Rec Src Acct', editable: false, filter: 'agSetColumnFilter' },
    { field: 'IDENTIFIER', headerName: 'Rec ID', editable: false, filter: 'agSetColumnFilter' },
    { field: 'ACCOUNT_ID', headerName: 'CW Acct ID', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CUSTODY_IDENTIFIER', headerName: 'Cust ID', editable: false, filter: 'agSetColumnFilter' },
    { field: 'CW_FV', headerName: 'CW FV', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value },
    { field: 'CU_FV', headerName: 'CUST FV', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value },
    { field: 'REC_FV_DIFF', headerName: 'FV Diff', editable: false, type: 'number', filter: 'agNumberColumnFilter', valueFormatter: (params) => params.value != null ? Number(params.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : params.value },
    { field: 'EXCLUDED_REASON', headerName: 'Excluded Reason', editable: false, filter: 'agTextColumnFilter', minWidth: 200, flex: 1 },
    { field: 'ASOFDT', headerName: 'As-of Date', editable: false, type: 'date', filter: 'agDateColumnFilter', valueFormatter: (params) => params.value ? formatDate(params.value) : params.value },
    { field: 'MODIFIED_BY', headerName: 'Modified By', editable: false, filter: 'agSetColumnFilter' },
    { field: 'MODIFIED_DATE', headerName: 'Modified Date', editable: false, type: 'date', filter: 'agDateColumnFilter', valueFormatter: (params) => params.value ? formatDateTimeForDisplay(params.value) : params.value },
  ], []);

  const handleDeleteClick = useCallback(() => {
    if (selectedCount === 0) {
      showNotification('Please select at least one row to delete', 'warning');
      return;
    }
    setDeleteDialogOpen(true);
  }, [selectedCount, showNotification]);

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setIsDeleting(true);
      setDeleteDialogOpen(false);
      const deletions = getSelectedData(rowData).map(row => ({
        CUSTODIAN: row.CUSTODIAN,
        SOURCE_ACCOUNT: row.SOURCE_ACCOUNT,
        IDENTIFIER: row.IDENTIFIER,
        ACCOUNT_ID: row.ACCOUNT_ID,
        CUSTODY_IDENTIFIER: row.CUSTODY_IDENTIFIER,
        ASOFDT: row.ASOFDT ? formatDate(row.ASOFDT) : row.ASOFDT,
        EXCLUDED_REASON: row.EXCLUDED_REASON,
        MODIFIED_BY: userId,
        MODIFIED_DATE: new Date().toISOString()
      }));
      await deleteExclusions(deletions);
      clearSelection();
      showNotification(`Successfully deleted ${deletions.length} exclusion(s)`, 'success');
    } catch (error) {
      console.error("Error deleting exclusions:", error);
      if (!notifyPermissionError(showNotification, error)) {
        showNotification("Failed to delete exclusions. Please try again.", 'error');
      }
    } finally {
      setIsDeleting(false);
      await loadEntries();
    }
  }, [getSelectedData, rowData, userId, clearSelection, loadEntries, showNotification]);

  // Own the hook here so we can bridge onGridReady to PamGrid — see the
  // matching note in ReconList.jsx for the grid-ready race rationale.
  const gridConfigs = useGridConfigs(api, {
    app: 'recon',
    gridId: 'exclusions',
    gridApiRef: gridRef,
  });

  const customToolbarComponents = useMemo(() => [
    <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />,
  ], [gridConfigs]);

  return (
    <Box sx={{ height: `${Math.max(height - 100, 300)}px` }}>
      <PamGrid
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        getRowId={getExcludedRowId}
        height={`${Math.max(height - 200, 200)}px`}
        customToolbarComponents={customToolbarComponents}
        onGridReady={gridConfigs.onGridReady}
        // Mirror every column/sort/filter gesture into the local stash so
        // unsaved tweaks survive a page refresh. See ReconList for context.
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}
        // Toolbar
        showToolbar
        title="Excluded Positions"
        showExport
        exportConfig={{
          fileName: `ExcludedPositions_${new Date().toISOString().split('T')[0]}.xlsx`,
          sheetName: 'Excluded Positions',
        }}
        showBulkDelete
        onBulkDelete={handleDeleteClick}
        selectedRowCount={selectedCount}
        // Selection column
        showSelectionColumn
        selectedRows={selectedRows}
        onRowSelectionChange={setSelectedRows}
        isSelectionDisabled={isLoading || isDeleting}
        // Quick Filter
        showQuickFilter
        quickFilterText={quickFilterText}
        onQuickFilterChange={setQuickFilterText}
        quickFilterPlaceholder="Quick Filter..."
        // State
        isLoading={isLoading}
        loadingMessage="Loading excluded positions..."
        // Additional modules
        modules={[SetFilterModule]}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Exclusions"
        message={`Are you sure you want to delete ${selectedCount} exclusion(s)? This action cannot be undone.`}
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        confirmColor="error"
      />
    </Box>
  );
};

export default ExcludedPositions;
